import { toCamelCase } from '@/utils/caseTransformer';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchSettings as fetchSettingsApi,
  updateSettings as updateSettingsApi,
  fetchPeriods as fetchPeriodsApi,
} from '../../services/payrollFoundationService';
import type { PayloadAction } from '@reduxjs/toolkit';

interface PayrollSettingsState {
  settings: any | null;
  periods: any[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  /**
   * Number of in-flight requests. A single boolean was cleared by whichever
   * thunk settled first, so fetchSettings + fetchPeriods running together
   * (both are dispatched from the settings list/detail pages) dropped the
   * spinner while the other request was still running.
   */
  pendingCount: number;
}

const initialState: PayrollSettingsState = {
  settings: null,
  periods: [],
  loading: false,
  saving: false,
  error: null,
  pendingCount: 0,
};

/**
 * `error.response.data` is a raw backend envelope (and `state.error` is
 * rendered straight into the UI), so rejecting with it stores an object where
 * a message string is expected and can leak server internals. Normalize to a
 * short, user-facing string.
 */
function toSafeError(error: any): string {
  const payload = error?.response?.data;
  const message =
    (typeof payload === 'string' ? payload : payload?.message) ??
    (typeof error?.message === 'string' ? error.message : '');
  return message && message.length <= 300 ? message : 'An unexpected error occurred.';
}

/**
 * Normalize the PayrollSettingResource payload to the camelCase
 * shape the PayrollSettings form component expects.
 */
function mapSettings(payload: any): any {
  if (!payload) return payload;
  const settings = toCamelCase<Record<string, any>>(payload);
  return {
    id: settings.id,
    policyConfirmationRequired: settings.policyConfirmationRequired ?? false,
    policyConfirmedAt: settings.policyConfirmedAt ?? null,
    maxEncashableDays: settings.maxEncashableDays ?? null,
    payrollExpenseAccountId: settings.payrollExpenseAccountId ?? null,
    salariesPayableAccountId: settings.salariesPayableAccountId ?? null,
    socialInsuranceExpenseAccountId: settings.socialInsuranceExpenseAccountId ?? null,
    socialInsurancePayableAccountId: settings.socialInsurancePayableAccountId ?? null,
    taxPayableAccountId: settings.taxPayableAccountId ?? null,
    employeeLoanAccountId: settings.employeeLoanAccountId ?? null,
    eosbExpenseAccountId: settings.eosbExpenseAccountId ?? null,
    eosbProvisionAccountId: settings.eosbProvisionAccountId ?? null,
    leaveProvisionAccountId: settings.leaveProvisionAccountId ?? null,
    defaultCostCenterId: settings.defaultCostCenterId ?? null,
    payrollCurrencyId: settings.payrollCurrencyId ?? null,
    overtimeRateNormal: settings.overtimeRateNormal ?? '1.50',
    overtimeRateHoliday: settings.overtimeRateHoliday ?? '2.00',
    workingDaysPerMonth: settings.workingDaysPerMonth ?? 30,
    workingHoursPerDay: settings.workingHoursPerDay ?? '8.00',
    eosbCalculationMethod: settings.eosbCalculationMethod ?? 'flat',
    eosbBaseSalaryMonths: settings.eosbBaseSalaryMonths ?? '0.50',
    approvalCfoThreshold: settings.approvalCfoThreshold ?? null,
    approvalGmThreshold: settings.approvalGmThreshold ?? null,
    isComplete: settings.isComplete ?? false,
    missingFields: settings.missingFields ?? [],
    version: settings.version,
    createdAt: settings.createdAt,
    updatedAt: settings.updatedAt,
  };
}

export const fetchSettings = createAsyncThunk(
  'payrollFoundation/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchSettingsApi();
      const data = response.data?.data ?? response.data;
      return mapSettings(data);
    } catch (error: any) {
      return rejectWithValue(toSafeError(error));
    }
  }
);

export const updateSettings = createAsyncThunk(
  'payrollFoundation/updateSettings',
  async (form: any, { rejectWithValue }) => {
    try {
      // Convert camelCase form back to the snake_case API payload. Fields the
      // form never carried must be OMITTED, not sent as `undefined`/`null` —
      // sending them would clear stored settings or trip required validation.
      // Explicit `null`s (cleared fields) are kept, since that is the intent.
      const mappedFields = {
        version: form.version,
        confirm_policy: form.confirmPolicy,
        max_encashable_days: form.maxEncashableDays,
        change_reason: form.changeReason,
        payroll_expense_account_id: form.payrollExpenseAccountId,
        salaries_payable_account_id: form.salariesPayableAccountId,
        social_insurance_expense_account_id: form.socialInsuranceExpenseAccountId,
        social_insurance_payable_account_id: form.socialInsurancePayableAccountId,
        tax_payable_account_id: form.taxPayableAccountId,
        employee_loan_account_id: form.employeeLoanAccountId,
        eosb_expense_account_id: form.eosbExpenseAccountId,
        eosb_provision_account_id: form.eosbProvisionAccountId,
        leave_provision_account_id: form.leaveProvisionAccountId,
        default_cost_center_id: form.defaultCostCenterId,
        payroll_currency_id: form.payrollCurrencyId,
        overtime_rate_normal: form.overtimeRateNormal,
        overtime_rate_holiday: form.overtimeRateHoliday,
        working_days_per_month: form.workingDaysPerMonth,
        working_hours_per_day: form.workingHoursPerDay,
        eosb_calculation_method: form.eosbCalculationMethod,
        eosb_base_salary_months: form.eosbBaseSalaryMonths,
        approval_cfo_threshold: form.approvalCfoThreshold,
        approval_gm_threshold: form.approvalGmThreshold,
      };
      const payload = Object.fromEntries(
        Object.entries(mappedFields).filter(([, value]) => value !== undefined),
      );
      const response = await updateSettingsApi(payload);
      const data = response.data?.data ?? response.data;
      return mapSettings(data);
    } catch (error: any) {
      return rejectWithValue(toSafeError(error));
    }
  }
);

export const fetchPeriods = createAsyncThunk(
  'payrollFoundation/fetchPeriods',
  async (params: any = { limit: -1 }, { rejectWithValue }) => {
    try {
      const response = await fetchPeriodsApi(params);
      const data = response.data?.data ?? response.data;
      return Array.isArray(data) ? data : data?.payroll_periods || data?.payrollPeriods || data?.periods || data?.data || [];
    } catch (error: any) {
      return rejectWithValue(toSafeError(error));
    }
  }
);

const payrollFoundationSlice = createSlice({
  name: 'payrollFoundation',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Reference-counted: `loading` clears only when the last in-flight request
    // settles, so one thunk finishing cannot hide another's spinner.
    const startRequest = (state: PayrollSettingsState) => {
      state.pendingCount += 1;
      state.loading = true;
      state.error = null;
    };
    const endRequest = (state: PayrollSettingsState, action: PayloadAction<unknown>) => {
      state.pendingCount = Math.max(0, state.pendingCount - 1);
      state.loading = state.pendingCount > 0;
      state.error = typeof action.payload === 'string' ? action.payload : null;
    };
    const endRequestOk = (state: PayrollSettingsState) => {
      state.pendingCount = Math.max(0, state.pendingCount - 1);
      state.loading = state.pendingCount > 0;
    };

    builder
      .addCase(fetchSettings.pending, startRequest)
      .addCase(fetchSettings.fulfilled, (state, action) => {
        endRequestOk(state);
        state.settings = action.payload;
      })
      .addCase(fetchSettings.rejected, endRequest)
      .addCase(fetchPeriods.pending, startRequest)
      .addCase(fetchPeriods.fulfilled, (state, action) => {
        endRequestOk(state);
        state.periods = action.payload;
      })
      .addCase(fetchPeriods.rejected, endRequest)
      .addCase(updateSettings.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateSettings.fulfilled, (state, action) => {
        state.saving = false;
        state.settings = action.payload;
      })
      .addCase(updateSettings.rejected, (state, action) => {
        state.saving = false;
        state.error = typeof action.payload === 'string' ? action.payload : null;
      });
  },
});

export const { clearError } = payrollFoundationSlice.actions;
export default payrollFoundationSlice.reducer;
